/**
 * Category configuration for shareable items.
 *
 * `ITEM_CATEGORY_OPTIONS` is the canonical ordered list used in dropdowns.
 * `CATEGORY_ALIASES` maps human-readable strings (including what external
 * barcode APIs may return) to the matching DB enum value, enabling fuzzy
 * matching without requiring exact enum strings from the user or the API.
 */
import type { Database } from '@/integrations/supabase/types';

export type ItemCategory = Database['public']['Enums']['item_category'];

export interface ItemCategoryOption {
  value: ItemCategory;
  label: string;
  description?: string;
}

export const ITEM_CATEGORY_OPTIONS: ItemCategoryOption[] = [
  { value: 'TOOL', label: 'Tool', description: 'Hand tools, power tools, and workshop equipment.' },
  {
    value: 'BOARD_GAME',
    label: 'Board Game',
    description: 'Tabletop and card-based games for groups.',
  },
  { value: 'PUZZLE', label: 'Puzzle', description: 'Jigsaws and other puzzle formats.' },
  { value: 'BOOK', label: 'Book', description: 'Fiction, non-fiction, and reference books.' },
  { value: 'TOY', label: 'Toy', description: 'Play items for kids and families.' },
  {
    value: 'EVENT_PARTY',
    label: 'Event & Party',
    description: 'Supplies and gear for gatherings and events.',
  },
];

const CATEGORY_ALIASES: Record<string, ItemCategory> = {
  tool: 'TOOL',
  tools: 'TOOL',
  'board game': 'BOARD_GAME',
  'board games': 'BOARD_GAME',
  game: 'BOARD_GAME',
  games: 'BOARD_GAME',
  puzzle: 'PUZZLE',
  puzzles: 'PUZZLE',
  book: 'BOOK',
  books: 'BOOK',
  toy: 'TOY',
  toys: 'TOY',
  event: 'EVENT_PARTY',
  party: 'EVENT_PARTY',
  'event party': 'EVENT_PARTY',
};

// Strips underscores, dashes, and extra whitespace so "board_game", "Board-Game",
// and "board game" all normalise to "board game" before alias lookup.
const normalizeCategoryText = (value: string) =>
  value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

/**
 * Type guard that narrows the given category to the `'TOOL'` enum value.
 *
 * @param category - An `ItemCategory` value, empty string, `null`, or `undefined`.
 * @returns `true` when `category` is exactly `'TOOL'`.
 */
export const isToolCategory = (
  category: ItemCategory | '' | null | undefined,
): category is 'TOOL' => category === 'TOOL';

/**
 * Converts a free-text string to an `ItemCategory` enum value via fuzzy
 * matching. Normalises underscores, dashes, and whitespace before looking up
 * the `CATEGORY_ALIASES` map, then falls back to label and enum value
 * matching against `ITEM_CATEGORY_OPTIONS`.
 *
 * Primarily used to map category strings returned by external barcode APIs
 * to internal enum values.
 *
 * @param value - The free-text category string to resolve.
 * @returns The matching `ItemCategory`, or `undefined` if no match is found.
 */
export const findItemCategoryByText = (value: string): ItemCategory | undefined => {
  const normalized = normalizeCategoryText(value);
  if (!normalized) return undefined;

  const matchedAlias = CATEGORY_ALIASES[normalized];
  if (matchedAlias) return matchedAlias;

  // Fall back to checking normalized label and enum value directly.
  return ITEM_CATEGORY_OPTIONS.find((option) => {
    const label = normalizeCategoryText(option.label);
    const enumValue = normalizeCategoryText(option.value);
    return normalized === label || normalized === enumValue;
  })?.value;
};
